<template>
  <q-page class="courses-page">
    <div class="courses-container">
      <!-- Header -->
      <div class="courses-header">
        <h1 class="courses-title">Catálogo de Cursos</h1>
        <p class="courses-subtitle">Explorá los cursos disponibles y comenzá tu formación</p>
      </div>

      <!-- Loading skeleton -->
      <div v-if="loading" class="courses-grid">
        <div v-for="n in 6" :key="n" class="course-card-skeleton">
          <q-skeleton type="rect" height="8px" width="80px" dark style="border-radius: 9999px; margin-bottom: 12px;" />
          <q-skeleton type="text" width="70%" dark style="font-size: 1.125rem;" />
          <q-skeleton type="text" dark style="margin-top: 4px;" />
          <q-skeleton type="text" width="60%" dark />
          <div style="display: flex; gap: 16px; margin-top: 16px;">
            <q-skeleton type="text" width="50px" dark />
            <q-skeleton type="text" width="60px" dark />
            <q-skeleton type="text" width="50px" dark />
          </div>
          <q-skeleton type="rect" height="36px" dark style="border-radius: 12px; margin-top: 16px;" />
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="courses-empty-state">
        <div class="courses-empty-icon-wrap">
          <q-icon name="error_outline" size="32px" style="color: var(--oa-danger, var(--color-coral));" />
        </div>
        <h2 class="courses-empty-title">Error al cargar cursos</h2>
        <p class="courses-empty-text">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          class="courses-retry-btn"
          @click="fetchData"
        />
      </div>

      <!-- Empty state -->
      <div v-else-if="courses.length === 0" class="courses-empty-state">
        <div class="courses-empty-icon-wrap">
          <q-icon name="school" size="32px" style="color: var(--oa-gold-full, var(--color-ocean-light));" />
        </div>
        <h2 class="courses-empty-title">No hay cursos disponibles</h2>
        <p class="courses-empty-text">Los cursos estarán disponibles próximamente.</p>
      </div>

      <!-- Course grid -->
      <div v-else class="courses-grid">
        <div
          v-for="course in coursesWithEnrollment"
          :key="course.id"
          class="course-card"
        >
          <!-- Badges -->
          <div class="course-card-badges">
            <span v-if="course.status === 'draft'" class="badge-outline">Borrador</span>
          </div>

          <!-- Title -->
          <h3 class="course-card-title">{{ course.title }}</h3>

          <!-- Short description -->
          <p class="course-card-desc">
            {{ course.shortDescription || course.description || 'Sin descripción disponible' }}
          </p>

          <!-- Stats row -->
          <div class="course-card-stats">
            <div class="course-card-stat">
              <q-icon name="folder_open" size="14px" />
              <span>{{ course.totalUnits }} unidades</span>
            </div>
            <div class="course-card-stat">
              <q-icon name="article" size="14px" />
              <span>{{ course.totalLessons }} lecciones</span>
            </div>
            <div v-if="course.estimatedHours" class="course-card-stat">
              <q-icon name="schedule" size="14px" />
              <span>~{{ course.estimatedHours }}h</span>
            </div>
          </div>

          <!-- Enrolled: progress bar + Continue -->
          <div v-if="course.isEnrolled" class="course-card-enrolled">
            <div class="course-progress-track">
              <div
                class="course-progress-fill"
                :style="{ width: course.progressPct + '%' }"
              />
            </div>
            <div class="course-progress-label">
              {{ Math.round(course.progressPct) }}% completado
            </div>
            <router-link :to="`/learn/${course.id}`" class="course-card-cta-link">
              <q-btn
                no-caps
                unelevated
                label="Continuar"
                class="course-card-cta-btn"
                icon-right="arrow_forward"
              />
            </router-link>
          </div>

          <!-- Not enrolled: price/free + Ver curso -->
          <div v-else class="course-card-not-enrolled">
            <span v-if="course.isFree" class="badge-free">Gratis</span>
            <span v-else-if="course.priceArs" class="course-card-price">
              ${{ Number(course.priceArs).toLocaleString('es-AR') }} ARS
            </span>
            <router-link :to="`/courses/${course.slug}`" class="course-card-cta-link">
              <q-btn
                no-caps
                unelevated
                label="Ver curso"
                class="course-card-cta-btn"
                icon-right="arrow_forward"
              />
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { api } from 'boot/axios';
import { useAuthStore } from 'stores/useAuthStore';

interface Course {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  shortDescription: string | null;
  coverImageUrl: string | null;
  status: string;
  priceArs: string | null;
  priceUsd: string | null;
  isFree: boolean;
  requiredLevel: string;
  totalUnits: number;
  totalLessons: number;
  estimatedHours: number | null;
  createdAt: string;
}

interface Enrollment {
  id: string;
  userId: string;
  courseId: string;
  status: string;
  progressPct: number;
  enrolledAt: string;
  course: { id: string; title: string; slug: string };
}

const authStore = useAuthStore();
const courses = ref<Course[]>([]);
const enrollments = ref<Enrollment[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);

const coursesWithEnrollment = computed(() => {
  const enrollmentMap = new Map<string, Enrollment>();
  for (const e of enrollments.value) {
    enrollmentMap.set(e.courseId, e);
  }

  return courses.value.map((course) => {
    const enrollment = enrollmentMap.get(course.id);
    return {
      ...course,
      isEnrolled: !!enrollment,
      progressPct: enrollment?.progressPct ?? 0,
    };
  });
});

async function fetchData() {
  loading.value = true;
  error.value = null;
  try {
    const requests: Promise<unknown>[] = [
      api.get<Course[]>('/courses'),
    ];

    if (authStore.isAuthenticated) {
      requests.push(api.get<Enrollment[]>('/learn/enrollments'));
    }

    const results = await Promise.all(requests);

    courses.value = (results[0] as { data: Course[] }).data;

    if (results[1]) {
      enrollments.value = (results[1] as { data: Enrollment[] }).data;
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Error desconocido';
    error.value = message;
  } finally {
    loading.value = false;
  }
}

onMounted(fetchData);
</script>

<style lang="scss" scoped>
.courses-page {
  display: flex;
  justify-content: center;
  padding: 24px 16px;
}

.courses-container {
  max-width: 960px;
  width: 100%;
}

// --- Header ---
.courses-header {
  margin-bottom: 28px;
}

.courses-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--oa-text-primary, var(--color-deep-charcoal));
  margin: 0;
  line-height: 1.3;
}

.courses-subtitle {
  font-size: 0.75rem;
  color: var(--oa-text-muted, var(--color-olive-stone));
  margin: 2px 0 0;
}

// --- Grid ---
.courses-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@media (min-width: 768px) {
  .courses-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .courses-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

// --- Course Card ---
.course-card {
  background: var(--oa-bg-2, var(--color-sandy-beige));
  border: 1px solid color-mix(in srgb, var(--oa-border, rgba(13, 43, 69, 0.12)) 50%, transparent);
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: color-mix(in srgb, var(--oa-gold-full, var(--color-ocean-light)) 30%, transparent);
    box-shadow: 0 0 20px rgba(46, 127, 181, 0.06);
  }
}

.course-card-skeleton {
  background: var(--oa-bg-2, var(--color-sandy-beige));
  border: 1px solid color-mix(in srgb, var(--oa-border, rgba(13, 43, 69, 0.12)) 50%, transparent);
  border-radius: 16px;
  padding: 20px;
}

// --- Badges ---
.course-card-badges {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.badge-gold {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
  background: linear-gradient(135deg, var(--oa-gold-dim, rgba(26, 74, 107, 0.55)), var(--oa-gold-full, var(--color-ocean-light)));
  color: var(--oa-bg-0, var(--color-white-cap));
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.badge-outline {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 500;
  border: 1px solid var(--oa-border, rgba(13, 43, 69, 0.12));
  color: var(--oa-text-secondary, var(--color-charcoal-mist));
}

.badge-free {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
  background: color-mix(in srgb, var(--oa-success, var(--oa-success)) 15%, transparent);
  color: var(--oa-success, var(--oa-success));
}

// --- Card Title ---
.course-card-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--oa-text-primary, var(--color-deep-charcoal));
  margin: 0 0 6px;
  line-height: 1.3;
}

// --- Card Description ---
.course-card-desc {
  font-size: 0.8125rem;
  color: var(--oa-text-secondary, var(--color-charcoal-mist));
  margin: 0 0 14px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex-grow: 1;
}

// --- Stats ---
.course-card-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.course-card-stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: var(--oa-text-muted, var(--color-olive-stone));
}

// --- Enrolled state ---
.course-card-enrolled {
  margin-top: auto;
}

.course-progress-track {
  height: 6px;
  border-radius: 3px;
  background: var(--oa-bg-3, var(--color-warm-stone));
  overflow: hidden;
  margin-bottom: 6px;
}

.course-progress-fill {
  height: 100%;
  border-radius: 3px;
  background: linear-gradient(90deg, var(--oa-gold-mid, var(--color-ocean-mid)), var(--oa-gold-full, var(--color-ocean-light)));
  transition: width 0.3s ease;
}

.course-progress-label {
  font-size: 0.7rem;
  color: var(--oa-text-muted, var(--color-olive-stone));
  font-family: var(--oa-font-mono, 'JetBrains Mono', monospace);
  font-variant-numeric: tabular-nums;
  margin-bottom: 12px;
}

// --- Not enrolled state ---
.course-card-not-enrolled {
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.course-card-price {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--oa-text-primary, var(--color-deep-charcoal));
}

// --- CTA Button ---
.course-card-cta-link {
  text-decoration: none;
  display: block;
}

.course-card-cta-btn {
  background: var(--oa-gold-full, var(--color-ocean-light)) !important;
  color: var(--color-white-cap) !important;
  font-weight: 600;
  border-radius: 12px;
  padding: 6px 20px;
  font-size: 0.8125rem;
  width: 100%;
}

.course-card-enrolled .course-card-cta-btn {
  width: 100%;
}

// --- Empty state ---
.courses-empty-state {
  text-align: center;
  padding: 48px 16px;
}

.courses-empty-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--oa-gold-full, var(--color-ocean-light)) 12%, transparent);
}

.courses-empty-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--oa-text-primary, var(--color-deep-charcoal));
  margin: 0 0 8px;
}

.courses-empty-text {
  font-size: 0.875rem;
  color: var(--oa-text-muted, var(--color-olive-stone));
  margin: 0 0 24px;
}

.courses-retry-btn {
  background: var(--oa-gold-full, var(--color-ocean-light)) !important;
  color: var(--color-white-cap) !important;
  font-weight: 600;
  border-radius: 12px;
}
</style>
